// The orders an operator can pick from: big rows, one per order, the one due soonest first
// (the server sorts). Every row is an order that still owes stock.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../apiHelpers';
import { formatEventDate } from '../../utils/date-utils';
import { BTN_TONAL, FOCUS, MOTION } from '../../Requisitions/buttonStyles';
import { OpenIcon } from '../icons';
import { FIELD, Notice } from '../ui';
import type { QueueOrder } from './types';

const TYPE_WORD = { sale: 'Sale', lease: 'Lease', rental: 'Rental' } as const;

const OrderRow: React.FC<{ order: QueueOrder; onPick: (o: QueueOrder) => void }> = ({ order, onPick }) => (
  <li>
    <button
      type="button"
      onClick={() => onPick(order)}
      className={`flex min-h-[4.5rem] w-full items-center gap-3 rounded-xl bg-surface-container px-4 py-3 text-left np-state ${FOCUS} ${MOTION}`}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-lg font-semibold text-on-surface" title={order.customer ?? undefined}>
          {order.customer || 'No customer name'}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-on-surface-variant">
          <span className="whitespace-nowrap font-mono tabular-nums">{order.so_number}</span>
          <span className="whitespace-nowrap rounded-full bg-surface-container-highest px-2 py-0.5 text-xs font-medium">{TYPE_WORD[order.order_type]}</span>
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block whitespace-nowrap text-base font-semibold tabular-nums text-on-surface">{order.expected} to ship</span>
        {order.date && (
          <span className="block whitespace-nowrap text-sm text-on-surface-variant">
            Ship {formatEventDate(order.date, 'MMM D')}
          </span>
        )}
      </span>
      <OpenIcon className="hidden h-5 w-5 shrink-0 text-on-surface-variant min-[400px]:block" />
    </button>
  </li>
);

interface Props {
  onPick: (order: QueueOrder) => void;
}

const ShipQueue: React.FC<Props> = ({ onPick }) => {
  const api = useApi();
  const [orders, setOrders] = useState<QueueOrder[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setFailed(false);
    setOrders(null);
    try {
      setOrders((await api.get('/orders/scanner-queue?direction=out')) as QueueOrder[]);
    } catch {
      setFailed(true);
    }
  }, []);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!orders || !q) return orders;
    return orders.filter((o) => (o.customer ?? '').toLowerCase().includes(q) || o.so_number.toLowerCase().includes(q));
  }, [orders, filter]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Notice tone="miss">The list did not load. Check the connection and try again.</Notice>
        <button type="button" onClick={load} className={`${BTN_TONAL} min-h-[2.75rem] px-5`}>Try again</button>
      </div>
    );
  }

  if (!orders) {
    return (
      <ul aria-label="Loading orders" className="flex flex-col gap-2">
        {[0, 1, 2].map((i) => (
          <li key={i} className="h-[4.5rem] rounded-xl bg-surface-container motion-safe:animate-pulse" />
        ))}
      </ul>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="rounded-xl bg-surface-container-low p-5 text-center">
        <p className="text-lg font-semibold text-on-surface">Nothing to ship right now</p>
        <p className="mt-1 text-sm text-on-surface-variant">An order shows up here once it is confirmed and still owes items.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <input
        type="search"
        aria-label="Find a customer or order"
        placeholder="Find a customer or order"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && shown && shown.length === 1) {
            e.preventDefault();
            onPick(shown[0]);
          }
        }}
        className={FIELD}
      />
      {shown && shown.length === 0 ? (
        <p role="status" className="rounded-xl bg-surface-container-low p-5 text-center text-base text-on-surface-variant break-words">
          Nothing matches &ldquo;{filter.trim()}&rdquo;
        </p>
      ) : (
        <ul aria-label="Orders to ship" className="flex flex-col gap-2">
          {(shown ?? []).map((o) => (
            <OrderRow key={o.id} order={o} onPick={onPick} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default ShipQueue;
